<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Button, Checkbox, Input, Radio, Select, Text, toast } from '@Shelly/shelly-design-system';
	//Services
	import { format } from 'svelte-i18n';
	import { localReq, postReq } from '../../../../services/http';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { capFirstChar, formatSnakeCaseToTitle } from '../../../../helpers/utilities';
	//Stores
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;

	const id = real_id(device.id);

	let settings = readDeviceSettings(id);
	let mode = settings.rollers[0].obstacle_mode;
	let action = settings.rollers[0].obstacle_action;
	let delay = settings.rollers[0].obstacle_delay;
	let power = settings.rollers[0].obstacle_power;
	let cacheMode = mode;
	let cacheAction = action;
	let cacheDelay = delay;
	let cachePower = power;
	let loading = false;

	$: disabled = mode === 'disabled';

	let modeOptions = ['disabled', 'while_opening', 'while_closing', 'while_moving'];
	let modeOptionsI18n = [
		$format('option_disabled'),
		$format('option_on_opening'),
		$format('option_on_closing'),
		$format('option_on_moving'),
	];
	let actionOptions = ['stop', 'reverse'];

	let actionsOptionsI18n = [$format('option_stop'), $format('option_reverse')];

	let min = 0;
	let maxTreshhold = 1840;

	let maxDelay = 1840;

	let localIp = get_local_ip(id);

	const setObstacle = async () => {
		if (
			cacheMode !== mode ||
			cacheAction !== action ||
			cacheDelay !== delay ||
			cachePower !== power
		) {
			let res;
			loading = true;
			let data = {
				obstacle_mode: mode,
				obstacle_action: action,
				obstacle_power: power,
				obstacle_delay: delay,
				roller_id: 0,
				id: id,
			};
			if (localIp) {
				delete data.id;
				delete data.roller_id;
				res = await localReq(device, `${localIp}/settings/roller/0`, 'post', data);
			} else {
				res = await postReq(`/device/relay/roller/settings/obstacle`, data);
			}

			if (res.isok) {
				settings.rollers[0].obstacle_mode = mode;
				settings.rollers[0].obstacle_action = action;
				settings.rollers[0].obstacle_power = power;
				settings.rollers[0].obstacle_delay = delay;
				devices_settings.update_devices_settings(id, settings);
				toast.open({
					type: 'success',
					title: disabled
						? $format('obstacle_mode_disabled')
						: $format('obstacle_save_scc_toast', {
								values: { mode: modeOptionsI18n[modeOptions.indexOf(mode)] },
								default: `${capFirstChar(device.type)} obstacle detection mode was ${
									!disabled ? 'changed to ' + formatSnakeCaseToTitle(mode) : 'disabled'
								}.`,
							}),
				});
				cacheMode = mode;
				cacheAction = action;
				cacheDelay = delay;
				cachePower = power;
			} else {
				mode = cacheMode;
				action = cacheAction;
				delay = cacheDelay;
				power = cachePower;
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			loading = false;
		}
	};

	const handleMode = (e) => {
		if (modeOptionsI18n.indexOf(e) === 0) {
			if (!disabled) {
				mode = modeOptions[modeOptionsI18n.indexOf(e)];
			} else {
				mode = modeOptions[1];
			}
		} else {
			mode = modeOptions[modeOptionsI18n.indexOf(e)];
		}
	};

	const handleAction = (e) => {
		action = actionOptions[actionsOptionsI18n.indexOf(e)];
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: setObstacle, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Checkbox
	size="m"
	checked={mode !== modeOptions[0]}
	on:click={() => handleMode(modeOptionsI18n[0])}
	id="checkbox-bbo73zs">{$format('label_enable')}</Checkbox
>
<Text styleType="body-small" class="mb-s">{$format('select_obstacle_detection_mode')}</Text>
{#each modeOptions as _mode, index}
	{#if index !== 0}
		<Radio
			size="m"
			{disabled}
			selected={modeOptions.indexOf(mode) === index}
			value={true}
			on:click={() => handleMode(modeOptionsI18n[index])}
			id="radio-ml1af1e">{modeOptionsI18n[index]}</Radio
		>
	{/if}
{/each}
<Text styleType="body-small" class="mb-s">{$format('obstacle_detection_action_msg')}</Text>
{#each actionOptions as _action, index}
	<Radio
		size="m"
		{disabled}
		selected={actionOptions.indexOf(action) === index}
		value={true}
		on:click={() => handleAction(actionsOptionsI18n[index])}
		id="radio-globjl5">{actionsOptionsI18n[index]}</Radio
	>
{/each}
<Text styleType="body-small" class="mb-s">
	{$format('obstacle_detection_threshold', {
		values: { min, max: maxTreshhold },
	})}
</Text>
<Input
	aria-label={$format('input_obstacle_detection_power', {
		default: 'Input Obstacle detection power',
	})}
	class="mb-s"
	size="m"
	{disabled}
	bind:value={power}
	type={'number'}
	max={maxTreshhold}
	on:enter={setObstacle}
	id="input-6xik3s5"
/>

<Text styleType="body-small" class="mb-s">
	{$format('obstacle_detection_delay', { values: { min, max: maxDelay } })}
</Text>
<Input
	aria-label={$format('input_obstacle_detection_delay', {
		default: 'Input Obstacle detection delay',
	})}
	size="m"
	{disabled}
	bind:value={delay}
	type={'number'}
	max={maxDelay}
	on:enter={setObstacle}
	id="input-o62olid"
/>

{#if !$showNewLayout}
	<Button class="mt-s" {loading} disabled={loading} size="m" width="full" on:click={setObstacle}>
		{$format('button_save')}
	</Button>
{/if}
